import { connectRoom } from './room-connection.js';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { createClient } from '@supabase/supabase-js';
import { ProcessingModal } from './processing-modal.jsx';
import { SafetyPanel } from './safety-panel.jsx';

const realtimeEvents = [
    'match.started',
    'item.used',
    'turn.submitted',
    'turn.changed',
    'match.finished',
    'rematch.updated',
    'rematch.created',
    'player.disconnected',
    'player.reconnected',
];

let battleSupabaseClient = null;

function BrandMark() {
    return (
        <span className="sd-brand-mark" aria-hidden="true">
            <svg className="h-7 w-7" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="10" stroke="currentColor" strokeWidth="2.2" />
                <path d="M16 6v3M26 16h-3M16 26v-3M6 16h3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                <circle cx="16" cy="16" r="3" fill="currentColor" />
                <path d="m18.2 13.9 4.1-3.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                <circle cx="23" cy="10.2" r="1.5" fill="currentColor" />
            </svg>
        </span>
    );
}

function ArrowUpIcon() {
    return (
        <svg aria-hidden="true" className="sd-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m7 11 5-5 5 5" />
            <path d="M12 18V6" />
        </svg>
    );
}

function readInitialState() {
    const element = document.getElementById('secret-digits-battle-state');
    if (!element?.textContent) {
        return null;
    }

    try {
        return JSON.parse(element.textContent);
    } catch {
        return null;
    }
}

function getSupabaseClient() {
    const url = import.meta.env.VITE_SUPABASE_URL
        || document.querySelector('meta[name="supabase-url"]')?.content;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
        || document.querySelector('meta[name="supabase-anon-key"]')?.content;

    if (!url || !anonKey) {
        return null;
    }

    if (battleSupabaseClient === null) {
        battleSupabaseClient = createClient(url, anonKey);
    }

    return battleSupabaseClient;
}

function uniqueDigits(value, digitLength) {
    const normalized = String(value || '').replace(/\D/g, '').slice(0, digitLength);
    return {
        value: normalized,
        complete: normalized.length === digitLength,
        duplicated: new Set(normalized.split('')).size !== normalized.length,
    };
}

function formatRemainingTime(deadlineAt) {
    const deadline = Date.parse(deadlineAt || '');
    if (Number.isNaN(deadline)) {
        return '--:--';
    }

    const remainingSeconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function reasonLabel(reason) {
    if (reason === 'abandoned') return '双方の再接続期限切れ';
    if (reason === 'timeout') {
        return 'タイムアウト';
    }
    if (reason === 'disconnect') {
        return '切断';
    }
    if (reason === 'surrender') {
        return '降参';
    }

    return '正解';
}

function ApiError({ message }) {
    if (!message) {
        return null;
    }

    return (
        <p className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
            {message}
        </p>
    );
}

function itemResultLabel(usage) {
    if (usage.item_id === 'parity_scan') {
        return (usage.result?.pattern || []).map((value) => value === 'even' ? '偶' : '奇').join('・');
    }
    if (usage.item_id === 'sum_range') {
        const labels = { low: 'LOW', middle: 'MIDDLE', high: 'HIGH' };
        return labels[usage.result?.range] || '-';
    }
    if (usage.item_id === 'slash') {
        return `最大−最小 = ${usage.result?.difference ?? '-'}`;
    }
    if (usage.item_id === 'target') {
        const digit = usage.result?.target_digit ?? '-';
        return usage.result?.included
            ? `${digit}は左から${usage.result?.position ?? '-'}桁目`
            : `${digit}は含まれない`;
    }
    return '-';
}

function itemName(itemId) {
    return {
        parity_scan: 'PARITY',
        slash: 'SLASH',
        target: 'TARGET',
        sum_range: '合計レンジ（旧）',
    }[itemId] || itemId;
}

function SimpleModal({ title, closeLabel = '閉じる', onClose, children, footer = null }) {
    const titleId = useId();
    const closeButtonRef = useRef(null);
    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        const previousPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        const bodyPaddingRight = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onCloseRef.current();
            }
        };

        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${bodyPaddingRight + scrollbarWidth}px`;
        }
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);
        closeButtonRef.current?.focus();

        return () => {
            document.body.style.overflow = previousOverflow;
            document.body.style.paddingRight = previousPaddingRight;
            document.removeEventListener('keydown', handleKeyDown);
            if (previousFocus instanceof HTMLElement) {
                previousFocus.focus();
            }
        };
    }, []);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-100/35 px-4 backdrop-blur-sm"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="w-full max-w-sm rounded-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl shadow-zinc-400/30"
            >
                <div className="flex items-start justify-between gap-4">
                    <h2 id={titleId} className="pt-1 text-base font-semibold text-zinc-100">{title}</h2>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-700 text-lg font-semibold text-zinc-300 hover:border-zinc-500 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-400"
                        onClick={onClose}
                        aria-label={closeLabel}
                    >
                        ×
                    </button>
                </div>
                <div className="mt-3 text-sm leading-relaxed text-zinc-300">{children}</div>
                {footer && <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{footer}</div>}
            </section>
        </div>
    );
}

function piePoint(angle, radius = 140) {
    const radians = (angle * Math.PI) / 180;

    return {
        x: 150 + radius * Math.cos(radians),
        y: 150 + radius * Math.sin(radians),
    };
}

function pieSlicePath(index, total) {
    if (total === 1) {
        return 'M 150 10 A 140 140 0 1 1 150 290 A 140 140 0 1 1 150 10 Z';
    }

    const startAngle = -90 + (360 * index) / total;
    const endAngle = -90 + (360 * (index + 1)) / total;
    const start = piePoint(startAngle);
    const end = piePoint(endAngle);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M 150 150 L ${start.x} ${start.y} A 140 140 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

function itemLabelLines(name) {
    return [name];
}

function CircularItemMenu({ items, disabled, usedItemIds, turnLocked, onSelect }) {
    const [highlightedItemId, setHighlightedItemId] = useState(null);
    const highlightedIndex = items.findIndex((item) => item.id === highlightedItemId);

    return (
        <svg
            viewBox="0 0 300 300"
            className="mx-auto block aspect-square w-full max-w-72"
            role="group"
            aria-label="アイテムを選択"
        >
            <circle cx="150" cy="150" r="144" className="fill-zinc-950 stroke-zinc-700 stroke-2" />
            {items.map((item, index) => {
                const middleAngle = -90 + (360 * (index + 0.5)) / items.length;
                const labelPosition = items.length === 1 ? { x: 150, y: 150 } : piePoint(middleAngle, 84);
                const lines = itemLabelLines(item.name);
                const used = usedItemIds.has(item.id);
                const itemDisabled = disabled || used || turnLocked;

                return (
                    <g
                        key={item.id}
                        data-item-id={item.id}
                        data-item-used={used ? 'true' : 'false'}
                        role="button"
                        tabIndex={itemDisabled ? -1 : 0}
                        aria-label={used ? `${item.name}（使用済み）` : `${item.name}を選択`}
                        aria-disabled={itemDisabled}
                        className={itemDisabled ? 'opacity-45' : 'group cursor-pointer focus:outline-none'}
                        onMouseEnter={() => !itemDisabled && setHighlightedItemId(item.id)}
                        onMouseLeave={() => setHighlightedItemId(null)}
                        onFocus={() => !itemDisabled && setHighlightedItemId(item.id)}
                        onBlur={() => setHighlightedItemId(null)}
                        onClick={() => !itemDisabled && onSelect(item)}
                        onKeyDown={(event) => {
                            if (!itemDisabled && (event.key === 'Enter' || event.key === ' ')) {
                                event.preventDefault();
                                onSelect(item);
                            }
                        }}
                    >
                        <path
                            d={pieSlicePath(index, items.length)}
                            className="fill-zinc-800 stroke-zinc-950 stroke-[4] transition group-hover:fill-red-950 group-focus:fill-red-950"
                        />
                        <text
                            x={labelPosition.x}
                            y={labelPosition.y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className="pointer-events-none select-none fill-zinc-100 text-[14px] font-semibold"
                        >
                            {lines.map((line, lineIndex) => (
                                <tspan
                                    key={line}
                                    x={labelPosition.x}
                                    dy={lineIndex === 0 ? (lines.length > 1 ? '-0.55em' : '0') : '1.2em'}
                                >
                                    {line}
                                </tspan>
                            ))}
                        </text>
                        {used && (
                            <g className="pointer-events-none" transform={`translate(${labelPosition.x} ${labelPosition.y + (lines.length > 1 ? 28 : 24)})`}>
                                <rect x="-29" y="-10" width="58" height="20" rx="10" className="fill-red-950 stroke-red-700" />
                                <text textAnchor="middle" dominantBaseline="middle" className="fill-red-100 text-[10px] font-semibold">使用済み</text>
                            </g>
                        )}
                    </g>
                );
            })}
            {highlightedIndex >= 0 && (
                <path
                    data-item-highlight="true"
                    d={pieSlicePath(highlightedIndex, items.length)}
                    className="pointer-events-none fill-none stroke-red-400 stroke-[4]"
                />
            )}
        </svg>
    );
}

function ItemPanel({ items, usages, processing, onUse }) {
    const [targetDigit, setTargetDigit] = useState('0');
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemUseResult, setItemUseResult] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const itemListId = useId();
    const usedThisTurn = usages.some((usage) => usage.turn_id == null);
    const usedItemIds = new Set(usages.map((usage) => usage.item_id));

    const confirmUse = async () => {
        if (!selectedItem) {
            return;
        }

        const item = selectedItem;
        setSelectedItem(null);
        setIsOpen(false);
        const usage = await onUse(item.id, item.id === 'target' ? Number(targetDigit) : null);
        if (usage) {
            setItemUseResult({ item, usage });
        }
    };

    return (
        <section className="rounded-xl border border-zinc-800 bg-zinc-900">
            <button
                type="button"
                className="flex min-h-12 w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-zinc-800/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-400 lg:hidden"
                onClick={() => setIsOpen((open) => !open)}
                aria-expanded={isOpen}
                aria-controls={itemListId}
                aria-label={isOpen ? 'アイテムを閉じる' : 'アイテムを開く'}
            >
                <span className="flex min-w-0 items-center gap-2">
                    <span className="text-sm font-semibold text-zinc-100">アイテム</span>
                    <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-xs font-semibold text-zinc-300">{items.length}個</span>
                </span>
                <span className="flex items-center gap-2">
                    <span className={`text-xs ${usedThisTurn ? 'text-zinc-500' : 'text-zinc-400'}`}>
                        {usedThisTurn ? '使用済み' : 'このターンで1つ使用可能'}
                    </span>
                    <span className="text-sm text-zinc-400" aria-hidden="true">{isOpen ? '▲' : '▼'}</span>
                </span>
            </button>
            <div className="hidden min-h-12 items-center justify-between gap-3 border-b border-zinc-800 px-4 py-3 lg:flex">
                <span className="text-sm font-semibold text-zinc-100">アイテム</span>
                <span className={`text-xs ${usedThisTurn ? 'text-zinc-500' : 'text-zinc-400'}`}>
                    {usedThisTurn ? 'このターンは使用済み' : '円から1つ選択'}
                </span>
            </div>
            <div id={itemListId} className={`${isOpen ? 'block' : 'hidden'} border-t border-zinc-800 lg:block lg:border-t-0`}>
                {items.length === 0 ? (
                    <p className="px-3 py-4 text-xs text-zinc-500">使用可能なアイテムはありません。</p>
                ) : (
                    <div className="px-3 py-4 lg:py-3">
                        <CircularItemMenu
                            items={items}
                            disabled={processing !== ''}
                            usedItemIds={usedItemIds}
                            turnLocked={usedThisTurn}
                            onSelect={setSelectedItem}
                        />
                    </div>
                )}
            </div>
            {selectedItem && (
                <SimpleModal
                    title={selectedItem.name}
                    closeLabel={`${selectedItem.name}の選択を閉じる`}
                    onClose={() => setSelectedItem(null)}
                    footer={(
                        <>
                            <button
                                type="button"
                                className="rounded-lg border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-200 hover:border-zinc-500 hover:bg-zinc-800"
                                onClick={() => setSelectedItem(null)}
                            >
                                キャンセル
                            </button>
                            <button
                                type="button"
                                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500"
                                onClick={confirmUse}
                                disabled={processing !== '' || usedThisTurn}
                                aria-label={`${selectedItem.name}を使う`}
                            >
                                使う
                            </button>
                        </>
                    )}
                >
                    <p className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-3 text-zinc-200">
                        {selectedItem.description}
                    </p>
                    {selectedItem.id === 'target' && (
                        <label className="mt-4 block text-sm font-semibold text-zinc-100">
                            調べる数字
                            <select
                                aria-label="TARGETで調べる数字"
                                value={targetDigit}
                                onChange={(event) => setTargetDigit(event.target.value)}
                                className="mt-2 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-zinc-100"
                            >
                                {Array.from({ length: 10 }, (_, digit) => <option key={digit} value={digit}>{digit}</option>)}
                            </select>
                        </label>
                    )}
                    <p className="mt-3 text-xs text-zinc-400">このターンでは、ほかのアイテムを使えなくなります。結果は両者に公開されます。</p>
                </SimpleModal>
            )}
            {itemUseResult && (
                <SimpleModal
                    title={`${itemUseResult.item.name}の結果`}
                    closeLabel="アイテム結果を閉じる"
                    onClose={() => setItemUseResult(null)}
                    footer={(
                        <button
                            type="button"
                            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-400"
                            onClick={() => setItemUseResult(null)}
                        >
                            閉じる
                        </button>
                    )}
                >
                    <p className="rounded-lg border border-red-900/70 bg-red-950/40 px-3 py-4 text-center font-mono text-lg font-semibold text-red-100">
                        {itemResultLabel(itemUseResult.usage)}
                    </p>
                    <p className="mt-3 text-xs text-zinc-400">この結果は相手にも公開されています。</p>
                </SimpleModal>
            )}
        </section>
    );
}

function DigitPad({ label, digitLength, value, disabled, processing, onChange, onSubmit, submitLabel, accessory = null }) {
    const state = uniqueDigits(value, digitLength);
    const duplicateDigits = state.value
        .split('')
        .filter((digit, index, digits) => digits.indexOf(digit) !== index || digits.lastIndexOf(digit) !== index);
    const canSubmit = state.complete && !state.duplicated && !disabled && !processing;

    const appendDigit = (digit) => {
        if (disabled || processing || state.value.length >= digitLength) {
            return;
        }

        onChange(`${state.value}${digit}`);
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            const activeTag = document.activeElement?.tagName?.toLowerCase();
            if (['input', 'textarea', 'select'].includes(activeTag || '') || event.metaKey || event.ctrlKey || event.altKey) {
                return;
            }

            if (/^\d$/.test(event.key)) {
                event.preventDefault();
                appendDigit(event.key);
                return;
            }

            if (event.key === 'Backspace') {
                event.preventDefault();
                onChange(state.value.slice(0, -1));
                return;
            }

            if (event.key === 'Enter') {
                event.preventDefault();
                if (canSubmit) {
                    onSubmit();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [canSubmit, disabled, onChange, onSubmit, processing, state.value]);

    return (
        <form
            className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(240px,280px)] lg:items-start"
            onSubmit={(event) => {
                event.preventDefault();
                if (canSubmit) {
                    onSubmit();
                }
            }}
        >
            <section className="order-2 rounded-xl border border-zinc-800 bg-zinc-900 p-4 lg:order-none lg:col-start-1 lg:row-start-1">
                <p className="text-sm font-semibold text-zinc-100">{label}</p>
                <input
                    value={state.value}
                    onChange={(event) => onChange(event.target.value)}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={digitLength}
                    className="sr-only"
                    aria-label={label}
                    disabled={disabled || processing}
                />
                <div className="mt-4 grid gap-3" style={{ gridTemplateColumns: `repeat(${digitLength}, minmax(0, 1fr))` }} aria-label={label}>
                    {Array.from({ length: digitLength }).map((_, index) => {
                        const digit = state.value[index] || '';
                        const duplicated = digit !== '' && duplicateDigits.includes(digit);

                        return (
                            <div
                                key={index}
                                className={[
                                    'flex h-16 items-center justify-center rounded-lg border font-mono text-3xl font-semibold shadow-inner shadow-black/30 sm:h-20 sm:text-4xl',
                                    duplicated ? 'border-rose-400 bg-rose-50 text-rose-800' : 'border-zinc-700 bg-zinc-950 text-zinc-100',
                                ].join(' ')}
                            >
                                {digit || '\u00a0'}
                            </div>
                        );
                    })}
                </div>
                <div className="mt-3 min-h-10" aria-live="polite">
                    {state.duplicated && (
                        <p className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
                            同じ数字は使えません。
                        </p>
                    )}
                </div>
            </section>

            {accessory && (
                <div className="order-1 lg:order-none lg:col-start-1 lg:row-start-2">
                    {accessory}
                </div>
            )}

            <section className="order-3 grid grid-cols-3 gap-2 lg:order-none lg:col-start-2 lg:row-span-2 lg:row-start-1" aria-label="テンキー">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                    <button
                        key={digit}
                        type="button"
                        className="h-14 touch-manipulation rounded-lg border border-zinc-700 bg-zinc-900 font-mono text-xl font-semibold text-zinc-100 hover:border-red-400 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 sm:h-16"
                        onClick={() => appendDigit(String(digit))}
                        disabled={disabled || processing}
                    >
                        {digit}
                    </button>
                ))}
                <button
                    type="button"
                    className="h-14 touch-manipulation rounded-lg border border-zinc-700 bg-zinc-900 text-sm font-semibold text-zinc-100 hover:border-red-400 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 sm:h-16"
                    onClick={() => onChange(state.value.slice(0, -1))}
                    disabled={disabled || processing}
                >
                    戻る
                </button>
                <button
                    type="button"
                    className="h-14 touch-manipulation rounded-lg border border-zinc-700 bg-zinc-900 font-mono text-xl font-semibold text-zinc-100 hover:border-red-400 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 sm:h-16"
                    onClick={() => appendDigit('0')}
                    disabled={disabled || processing}
                >
                    0
                </button>
                <button
                    type="button"
                    className="h-14 touch-manipulation rounded-lg border border-zinc-700 bg-zinc-900 text-sm font-semibold text-zinc-100 hover:border-red-400 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 sm:h-16"
                    onClick={() => onChange('')}
                    disabled={disabled || processing}
                >
                    クリア
                </button>
                <button
                    type="submit"
                    className="sd-button-primary col-span-3 inline-flex h-14 touch-manipulation items-center justify-center gap-2 bg-red-600 text-sm font-semibold text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40 sm:h-16"
                    disabled={!canSubmit}
                >
                    {submitLabel}<ArrowUpIcon />
                </button>
            </section>
        </form>
    );
}

function BattleHistory({ turns, itemUsages, playerId }) {
    const [tab, setTab] = useState('mine');
    const [pages, setPages] = useState({ mine: 0, opponent: 0 });
    const pageSize = 10;
    const playerRows = (targetPlayerId) => {
        const rowsByTurn = new Map();

        turns
            .filter((turn) => turn.actor_player_id === targetPlayerId)
            .forEach((turn) => rowsByTurn.set(turn.turn_no, { turn, itemUsage: null }));
        itemUsages
            .filter((usage) => usage.player_id === targetPlayerId)
            .forEach((usage) => {
                const row = rowsByTurn.get(usage.turn_no) || { turn: null, itemUsage: null };
                rowsByTurn.set(usage.turn_no, { ...row, itemUsage: usage });
            });

        return Array.from(rowsByTurn.entries())
            .map(([turnNo, row]) => ({ turnNo, ...row }))
            .sort((left, right) => right.turnNo - left.turnNo);
    };
    const mine = playerRows(playerId);
    const opponentPlayerId = turns.find((turn) => turn.actor_player_id !== playerId)?.actor_player_id
        || itemUsages.find((usage) => usage.player_id !== playerId)?.player_id;
    const opponent = opponentPlayerId ? playerRows(opponentPlayerId) : [];
    const rows = tab === 'mine' ? mine : opponent;
    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    const page = Math.min(pages[tab] || 0, pageCount - 1);
    const visibleRows = rows.slice(page * pageSize, (page + 1) * pageSize);

    const changePage = (nextPage) => {
        setPages((current) => ({ ...current, [tab]: Math.max(0, Math.min(nextPage, pageCount - 1)) }));
    };

    return (
        <section className="sd-surface rounded-2xl border border-zinc-800 bg-zinc-900 p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-semibold text-zinc-100">対戦履歴</h2>
                <div className="-mx-1 overflow-x-auto px-1 pb-1">
                    <div role="tablist" aria-label="対戦履歴の種類" className="inline-flex min-w-max rounded-lg border border-zinc-800 bg-zinc-950 p-1">
                        {[
                            ['mine', '自分のターン'],
                            ['opponent', '相手のターン'],
                        ].map(([key, text]) => (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                className={[
                                    'whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold',
                                    tab === key ? 'bg-zinc-100 text-zinc-950' : 'text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100',
                                ].join(' ')}
                                onClick={() => setTab(key)}
                                aria-selected={tab === key}
                            >
                                {text}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-800">
                <table className="w-full min-w-[42rem] text-left text-sm">
                    <thead className="bg-zinc-950 text-zinc-300">
                        <tr>
                            <th className="px-3 py-2">ターン</th>
                            <th className="px-3 py-2">推測</th>
                            <th className="px-3 py-2">Hit / Blow</th>
                            <th className="px-3 py-2">アイテム</th>
                            <th className="px-3 py-2">アイテム結果</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800 bg-zinc-900">
                        {visibleRows.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-3 py-4 text-center text-zinc-500">
                                    {tab === 'mine' ? 'あなたの履歴はまだありません。' : '相手の履歴はまだありません。'}
                                </td>
                            </tr>
                        ) : visibleRows.map(({ turnNo, turn, itemUsage }) => (
                            <tr key={turnNo}>
                                <td className="px-3 py-2 text-zinc-400">{turnNo}</td>
                                <td className="px-3 py-2 font-mono text-zinc-100">{turn?.guess || '-'}</td>
                                <td className="px-3 py-2 text-zinc-300">{turn ? `H${turn.hit_count} / B${turn.blow_count}` : '-'}</td>
                                <td className="px-3 py-2 text-zinc-200">{itemUsage ? itemName(itemUsage.item_id) : '-'}</td>
                                <td className="whitespace-nowrap px-3 py-2 font-mono font-semibold text-red-200">{itemUsage ? itemResultLabel(itemUsage) : '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {rows.length > pageSize && (
                <div className="mt-3 flex items-center justify-between gap-3 text-xs text-zinc-400">
                    <span>{page * pageSize + 1}-{Math.min(rows.length, (page + 1) * pageSize)} / {rows.length}</span>
                    <div className="flex gap-2">
                        <button type="button" className="rounded-md border border-zinc-700 px-3 py-1.5 font-semibold text-zinc-200 disabled:opacity-40" onClick={() => changePage(page - 1)} disabled={page === 0}>前へ</button>
                        <button type="button" className="rounded-md border border-zinc-700 px-3 py-1.5 font-semibold text-zinc-200 disabled:opacity-40" onClick={() => changePage(page + 1)} disabled={page >= pageCount - 1}>次へ</button>
                    </div>
                </div>
            )}
        </section>
    );
}

function BattleApp({ initial }) {
    if (initial.invalid_session) {
        return <ReconnectPanel initial={initial} />;
    }

    const [match, setMatch] = useState(initial.match);
    const [secret, setSecret] = useState('');
    const [guess, setGuess] = useState('');
    const [error, setError] = useState('');
    const [sessionClosed, setSessionClosed] = useState(false);
    const [processing, setProcessing] = useState('');
    const [realtimeStatus, setRealtimeStatus] = useState('fallback');
    const [connectionNotice, setConnectionNotice] = useState('');
    const connectionWasReady = useRef(false);
    const opponentWasMissing = useRef(Boolean(initial.match.players?.find(p => p.id !== initial.player_id)?.disconnected_at));
    const [remaining, setRemaining] = useState(formatRemainingTime(initial.match.turn_deadline_at));
    const mountedRef = useRef(true);
    const playerId = initial.player_id;
    const digitLength = Number(match.digit_length || 3);
    const players = match.players || [];
    const me = players.find((player) => player.id === playerId);
    const opponent = players.find((player) => player.id !== playerId);
    const isMyTurn = match.current_turn_player_id === playerId;
    const finished = match.status === 'finished';
    const rematchResolved = ['created', 'declined', 'opponent_left', 'expired'].includes(match.rematch_status);

    const fetchState = useCallback(async () => {
        try {
            const response = await fetch(`/api/v1/matches/${initial.match_id}`, {
                headers: {
                    Accept: 'application/json',
                    ...(initial.player_token ? { Authorization: `Bearer ${initial.player_token}` } : {}),
                },
            });

            if (!response.ok) {
                if (initial.backend === 'cloudflare' && [401, 404, 410].includes(response.status)) setSessionClosed(true);
                return;
            }

            const nextMatch = await response.json();
            if (!mountedRef.current) {
                return;
            }

            setMatch(nextMatch);
            if (nextMatch.next_match_id && nextMatch.rematch_status === 'created') {
                window.location.assign(initial.battle_url);
            }
        } catch {
            if (mountedRef.current) setRealtimeStatus('fallback');
        }
    }, [initial.battle_url, initial.match_id, initial.player_token]);

    const callApi = useCallback(async (path, body) => {
        setError('');
        const response = await fetch(path, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                ...(initial.player_token ? { Authorization: `Bearer ${initial.player_token}` } : {}),
                'X-CSRF-TOKEN': initial.csrf_token,
            },
            body: JSON.stringify(body),
        });
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
            if (payload.code === 'REMATCH_EXPIRED') {
                setMatch((current) => ({ ...current, rematch_status: 'expired' }));
            }
            const message = payload.code === 'REMATCH_EXPIRED' ? '再戦の受付期限が切れました。新しいルームで対戦してください。' : (payload.message || '操作に失敗しました。');
            throw new Error(message);
        }

        if (payload.match) {
            setMatch(payload.match);
        } else {
            await fetchState();
        }

        return payload;
    }, [fetchState, initial.csrf_token, initial.player_token]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const resume = () => {
            if (document.visibilityState === 'visible') {
                fetchState();
            }
        };
        document.addEventListener('visibilitychange', resume);
        window.addEventListener('pageshow', fetchState);
        return () => {
            document.removeEventListener('visibilitychange', resume);
            window.removeEventListener('pageshow', fetchState);
        };
    }, [fetchState]);

    useEffect(() => {
        const timer = window.setInterval(() => {
            setRemaining(formatRemainingTime(match.turn_deadline_at));
        }, 1000);

        return () => window.clearInterval(timer);
    }, [match.turn_deadline_at]);

    useEffect(() => {
        if (finished && rematchResolved) {
            return undefined;
        }

        const intervalMs = finished ? 30000 : (realtimeStatus === 'subscribed' ? 30000 : 8000);
        const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void fetchState(); }, intervalMs);

        return () => window.clearInterval(timer);
    }, [fetchState, finished, realtimeStatus, rematchResolved]);

    useEffect(() => {
        if (initial.backend === 'cloudflare') {
            if (finished || sessionClosed) return undefined;
            return connectRoom({ roomCode: initial.room_code, onState: fetchState, onStatus: setRealtimeStatus });
        }
        const client = getSupabaseClient();
        const topic = match.realtime_topic;

        if (!client || !topic) {
            setRealtimeStatus('fallback');
            return undefined;
        }

        const channel = client.channel(topic);
        realtimeEvents.forEach((eventName) => {
            channel.on('broadcast', { event: eventName }, fetchState);
        });

        channel.subscribe((status) => {
            setRealtimeStatus(status === 'SUBSCRIBED' ? 'subscribed' : 'fallback');
        });

        return () => {
            client.removeChannel(channel);
        };
    }, [fetchState, initial.backend, initial.room_code, match.realtime_topic, finished, sessionClosed]);

    const submitSecret = async () => {
        const validation = uniqueDigits(secret, digitLength);
        if (!validation.complete || validation.duplicated) {
            setError(`重複なしの${digitLength}桁で入力してください。`);
            return;
        }

        setProcessing('secret');
        try {
            await callApi(`/api/v1/matches/${match.match_id}/secret`, { secret: validation.value });
            setSecret('');
        } catch (apiError) {
            setError(apiError.message);
        } finally {
            setProcessing('');
        }
    };

    const submitGuess = async () => {
        const validation = uniqueDigits(guess, digitLength);
        if (!validation.complete || validation.duplicated) {
            setError(`重複なしの${digitLength}桁で入力してください。`);
            return;
        }

        setProcessing('guess');
        try {
            await callApi(`/api/v1/matches/${match.match_id}/guesses`, { guess: validation.value });
            setGuess('');
        } catch (apiError) {
            setError(apiError.message);
        } finally {
            setProcessing('');
        }
    };

    const useItem = async (itemId, targetDigit = null) => {
        setProcessing(`item-${itemId}`);
        try {
            const body = itemId === 'target' ? { target_digit: targetDigit } : {};
            const payload = await callApi(`/api/v1/matches/${match.match_id}/items/${encodeURIComponent(itemId)}/use`, body);
            return payload.item_usage || null;
        } catch (apiError) {
            setError(apiError.message);
            return null;
        } finally {
            setProcessing('');
        }
    };

    const requestRematch = async () => {
        setProcessing('rematch');
        try {
            const payload = await callApi(`/api/v1/matches/${match.match_id}/rematch-consents`, { agree: true });
            if (payload.rematch_status === 'created') {
                window.location.assign(initial.battle_url);
            }
        } catch (apiError) {
            setError(apiError.message);
        } finally {
            setProcessing('');
        }
    };

    const declineRematch = async () => {
        setProcessing('rematch-decline');
        try {
            await callApi(`/api/v1/matches/${match.match_id}/rematch-consents`, { agree: false, reason: 'declined' });
        } catch (apiError) {
            setError(apiError.message);
        } finally {
            setProcessing('');
        }
    };

    useEffect(() => {
        if (finished || sessionClosed) { setSecret(''); setGuess(''); }
    }, [finished, sessionClosed]);

    const returnHome = async () => {
        setProcessing('return-home');
        try {
            if (finished && !rematchResolved) {
                await callApi(`/api/v1/matches/${match.match_id}/rematch-consents`, { agree: false, reason: 'left' });
            }
        } catch {
            // ホームへ戻る操作は、離脱状態の保存に失敗しても妨げない。
        } finally {
            if (initial.backend === 'cloudflare') {
                try {
                    const response = await fetch('/api/web/session/clear', { method: 'POST', headers: { 'X-Secret-Digits-Web': '1', 'Content-Type': 'application/json' }, body: '{}', signal: AbortSignal.timeout(5000) });
                    if (!response.ok) throw new Error('Session deletion failed.');
                } catch {
                    setError('セッションを消去できませんでした。通信状況を確認して再試行してください。');
                    setProcessing('');
                    return;
                }
            }
            window.location.assign(initial.lobby_url);
        }
    };

    const surrender = async () => {
        if (!window.confirm('降参すると相手の勝利として確定します。降参しますか？')) {
            return;
        }

        setProcessing('surrender');
        try {
            await callApi(`/api/v1/matches/${match.match_id}/surrender`, {});
        } catch (apiError) {
            setError(apiError.message);
        } finally {
            setProcessing('');
        }
    };

    const result = useMemo(() => {
        const winner = players.find((player) => player.id === match.winner_player_id);
        return {
            winnerName: winner?.name || 'なし',
            isWinner: match.winner_player_id === playerId,
            reason: reasonLabel(match.win_reason),
            finalTurnNo: match.final_turn_no || 0,
        };
    }, [match.final_turn_no, match.win_reason, match.winner_player_id, playerId, players]);

    useEffect(() => {
        if (realtimeStatus === 'subscribed' && connectionWasReady.current === 'retry') setConnectionNotice('接続を回復しました。');
        if (realtimeStatus === 'subscribed') connectionWasReady.current = true;
        else if (connectionWasReady.current) connectionWasReady.current = 'retry';
        const missing = Boolean(opponent?.disconnected_at);
        if (opponentWasMissing.current && !missing && !finished) setConnectionNotice('相手が再接続しました。');
        opponentWasMissing.current = missing;
    }, [realtimeStatus, opponent?.disconnected_at, finished]);
    useEffect(() => {
        if (!connectionNotice) return undefined;
        const timer = window.setTimeout(() => setConnectionNotice(''), 4000);
        return () => window.clearTimeout(timer);
    }, [connectionNotice]);
    const opponentDisconnected = !finished && opponent?.disconnected_at;
    const ownDisconnected = !finished && me?.disconnected_at;
    const connectionMessage = ownDisconnected
        ? (Date.now() >= Date.parse(ownDisconnected) + 60000 ? '復帰の猶予が過ぎました。対戦の終了を確認しています。' : '接続を確認しています。回線が戻ると自動で再接続します。')
        : opponentDisconnected ? `相手の接続を確認中です。再接続を待っています（猶予 ${formatRemainingTime(new Date(Date.parse(opponentDisconnected) + 60000).toISOString())}）。手番の制限時間は進みます。`
        : !finished && realtimeStatus === 'fallback' ? 'リアルタイム接続を再確認中です。対戦情報は定期的に確認します。' : !finished ? connectionNotice : '';

    if (sessionClosed) return (
        <main className="mx-auto flex min-h-screen w-full max-w-2xl items-center px-4 py-8 sm:px-6">
            <section className="sd-surface w-full rounded-2xl border border-zinc-800 bg-zinc-900 p-6 sm:p-8" aria-labelledby="session-closed-title">
                <BrandMark />
                <h1 id="session-closed-title" className="mt-5 text-2xl font-bold text-zinc-50">このルームへの参加は終了しました</h1>
                <p className="mt-4 text-sm leading-relaxed text-zinc-300">参加情報の失効やルームの終了などにより、このルームには戻れません。ロビーから新しい対戦を始められます。</p>
                <div className="mt-6"><ApiError message={error} /></div>
                <button type="button" className="sd-button-primary mt-6 inline-flex min-h-12 w-full items-center justify-center bg-red-600 px-5 py-3 font-semibold text-white hover:bg-red-500 disabled:opacity-60" onClick={returnHome} disabled={processing !== ''}>ロビーへ戻る</button>
            </section>
        </main>
    );

    return (
        <main className="mx-auto flex min-h-screen w-full max-w-5xl flex-col gap-4 px-4 py-6 sm:gap-7 sm:px-6 sm:py-10 lg:px-8">
            <ProcessingModal show={processing !== ''} />
            {connectionMessage && <p role="status" className="mt-4 rounded-lg border border-zinc-700 bg-zinc-900 p-3 text-sm text-zinc-300">{connectionMessage}</p>}
            {initial.backend === 'cloudflare' && players.length === 2 && <SafetyPanel matchId={match.match_id} playerToken={initial.player_token} lobbyUrl={initial.lobby_url} />}
            <header className="sd-hero rounded-2xl border p-4 sm:p-7">
                <div className="relative z-10 flex items-center gap-4">
                    <BrandMark />
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-red-400">Secret Digits</p>
                        <h1 className="mt-1 text-2xl font-bold tracking-tight text-zinc-50 sm:text-4xl">2人対戦</h1>
                        <p className="mt-1 text-sm text-zinc-400">{digitLength}桁 ・ {match.ruleset === 'tactics' ? 'Tactics' : 'Classic'}</p>
                    </div>
                </div>
            </header>

            <section className="sd-surface rounded-2xl border border-zinc-800 bg-zinc-900 p-4 sm:p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-zinc-300">あなた: <span className="font-semibold text-zinc-100">{me?.name || initial.player_name}</span></p>
                    <p className="text-sm text-zinc-300">相手: <span className="font-semibold text-zinc-100">{opponent?.name || '参加待ち'}</span></p>
                </div>

                <div className="mt-5 space-y-4">
                    <ApiError message={error} />
                </div>

                {match.status === 'preparing' && (
                    <section className="mt-6 rounded-xl border border-zinc-800 bg-zinc-950 p-4">
                        <p className="text-sm text-zinc-300">秘密数字を登録してください（重複なし{digitLength}桁）。</p>
                        <p className="mt-2 text-xs text-zinc-400">
                            あなた: {me?.secret_submitted ? '登録済み' : '未登録'} / 相手: {opponent?.secret_submitted ? '登録済み' : '未登録'}
                        </p>
                        {!me?.secret_submitted && (
                            <div className="mt-4">
                                <DigitPad
                                    label="秘密数字"
                                    digitLength={digitLength}
                                    value={secret}
                                    disabled={false}
                                    processing={processing === 'secret'}
                                    onChange={setSecret}
                                    onSubmit={submitSecret}
                                    submitLabel="登録"
                                />
                            </div>
                        )}
                    </section>
                )}

                {match.status === 'in_progress' && (
                    <section className="mt-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-base font-semibold text-zinc-100">
                                    {isMyTurn ? 'あなたのターン' : `${opponent?.name || '相手'}のターン`}
                                </p>
                                <p className="mt-1 text-xs text-zinc-400">残り時間: <span className="font-mono font-semibold text-red-200">{remaining}</span></p>
                            </div>
                            <button type="button" className="self-start rounded-lg border border-zinc-700 px-3 py-2 text-xs font-semibold text-zinc-300 hover:border-red-400 hover:text-red-200 sm:self-auto" onClick={surrender} disabled={processing !== ''} aria-label="降参してホームに戻る">
                                降参
                            </button>
                        </div>

                        {isMyTurn ? (
                            <div className="mt-4">
                                <DigitPad
                                    label="推測入力"
                                    digitLength={digitLength}
                                    value={guess}
                                    disabled={processing !== ''}
                                    processing={processing === 'guess'}
                                    onChange={setGuess}
                                    onSubmit={submitGuess}
                                    submitLabel="送信"
                                    accessory={match.ruleset === 'tactics' && digitLength === 4 ? (
                                        <ItemPanel
                                            items={match.item_catalog || match.available_items || []}
                                            usages={(match.item_usages || []).filter((usage) => usage.player_id === playerId)}
                                            processing={processing}
                                            onUse={useItem}
                                        />
                                    ) : null}
                                />
                            </div>
                        ) : (
                            <div className="mt-4 flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950 px-4 py-4">
                                <div className="flex items-center gap-3">
                                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-zinc-400 border-t-transparent" aria-hidden="true"></span>
                                    <p className="text-sm text-zinc-300">相手の入力を待っています</p>
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {finished && (
                    <section className={`mt-6 rounded-xl border ${result.isWinner ? 'border-emerald-400/60 bg-emerald-50' : 'border-rose-300 bg-rose-50'} p-5`}>
                        <h2 className={`text-2xl font-bold ${result.isWinner ? 'text-emerald-700' : 'text-rose-700'}`}>{match.winner_player_id === null ? '勝敗なし' : result.isWinner ? '勝利' : '敗北'}</h2>
                        <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                            <div><dt className="text-zinc-500">勝者</dt><dd className="mt-1 font-semibold text-zinc-100">{result.winnerName}</dd></div>
                            <div><dt className="text-zinc-500">理由</dt><dd className="mt-1 font-semibold text-zinc-100">{result.reason}</dd></div>
                            <div><dt className="text-zinc-500">最終ターン</dt><dd className="mt-1 font-semibold text-zinc-100">{result.finalTurnNo || '-'}</dd></div>
                        </dl>
                        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                            <button type="button" className="inline-flex justify-center rounded-lg border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-100 hover:border-zinc-500 hover:bg-zinc-900 disabled:opacity-60" onClick={returnHome} disabled={processing !== ''}>ホームに戻る</button>
                            {match.rematch_status === 'waiting_for_opponent' ? (
                                <button type="button" disabled className="inline-flex justify-center rounded-lg border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-400">相手の同意待ち</button>
                            ) : match.rematch_status === 'created' ? (
                                <a href={initial.battle_url} className="inline-flex justify-center rounded-lg bg-zinc-100 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-white">次の準備画面へ</a>
                            ) : match.rematch_status === 'declined' ? (
                                <span className="inline-flex justify-center rounded-lg border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-400">再戦は不成立です</span>
                            ) : match.rematch_status === 'expired' ? (
                                <span className="inline-flex justify-center rounded-lg border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-400">再戦の受付期限が切れました</span>
                            ) : match.rematch_status === 'opponent_left' ? (
                                <span className="inline-flex justify-center rounded-lg border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-400">相手はすでにホームに戻りました</span>
                            ) : (
                                <>
                                    <button type="button" className="inline-flex justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-60" onClick={requestRematch} disabled={processing !== ''}>
                                        {match.rematch_status === 'requested_by_opponent' ? '再戦する' : '再戦を申し込む'}
                                    </button>
                                    {match.rematch_status === 'requested_by_opponent' && (
                                        <button type="button" className="inline-flex justify-center rounded-lg border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-200 hover:border-zinc-500 hover:bg-zinc-900 disabled:opacity-60" onClick={declineRematch} disabled={processing !== ''}>
                                            再戦しない
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </section>
                )}
            </section>

            <BattleHistory turns={match.turns || []} itemUsages={match.item_usages || []} playerId={playerId} />
        </main>
    );
}

function ReconnectPanel({ initial }) {
    const [token, setToken] = useState('');
    const [error, setError] = useState('');
    const [processing, setProcessing] = useState(false);

    const reconnect = async (event) => {
        event.preventDefault();
        setError('');
        setProcessing(true);

        try {
            const response = await fetch(initial.reconnect_url, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': initial.csrf_token,
                    ...(initial.backend === 'cloudflare' ? { 'X-Secret-Digits-Web': '1' } : {}),
                },
                body: JSON.stringify(initial.backend === 'cloudflare' ? {} : { reconnect_token: token }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload.message || '再接続に失敗しました。');
            }
            window.location.assign(payload.battle_url || initial.battle_url);
        } catch (apiError) {
            setError(apiError.message);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <main className="mx-auto flex min-h-screen w-full max-w-3xl flex-col justify-center px-4 py-10 sm:px-6 lg:px-8">
            <ProcessingModal show={processing} />
            <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
                <p className="text-sm text-rose-700">{initial.backend === 'cloudflare' ? 'このブラウザの参加情報で再接続できます。ルームの終了・ブロック・情報の失効後は復帰できません。' : 'このブラウザでは対戦参加情報を確認できません。再接続トークンで復帰するか、ロビーから参加し直してください。'}</p>
                <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={reconnect}>
                    {initial.backend !== 'cloudflare' && <input
                        type="text"
                        value={token}
                        onChange={(event) => setToken(event.target.value)}
                        disabled={processing}
                        className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-red-500 focus:outline-none"
                        placeholder="reconnect_token"
                    />}
                    <button
                        type="submit"
                        disabled={processing}
                        className="inline-flex justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-60"
                    >
                        再接続
                    </button>
                </form>
                {error && <p className="mt-2 text-xs font-semibold text-rose-700">{error}</p>}
                <a href={initial.join_url} className="mt-4 inline-flex rounded-lg border border-zinc-600 px-4 py-2 text-sm text-zinc-200">このルームへ参加し直す</a>
            </section>
        </main>
    );
}

export function mountBattleApp() {
    const root = document.getElementById('secret-digits-battle-root');
    const initial = readInitialState();

    if (!root || !initial || root.dataset.reactMounted === 'true') {
        return;
    }

    root.dataset.reactMounted = 'true';
    createRoot(root).render(<BattleApp initial={initial} />);
}
